import React, { lazy, Suspense, useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import { useIsImintUser } from 'features/imint/access';
import Layout from 'shared/components/Layout';
import ProtectedRoute from 'shared/components/ProtectedRoute';
import ErrorBoundary from 'shared/components/ErrorBoundary';
import ToastProvider from 'shared/components/ToastProvider';
import LoadingSpinner from 'shared/components/LoadingSpinner';
import TokenSync from 'shared/components/TokenSync';
import AnalyticsProvider from 'shared/components/AnalyticsProvider';
import SessionGuard from 'shared/components/SessionGuard';
import CrossTabSync from 'shared/components/CrossTabSync';
import { env } from 'config/env';

const LoginPage = lazy(() => import('features/auth/LoginPage'));
const RequestAccessPage = lazy(() => import('features/auth/RequestAccessPage'));
const VerifyEmailPage = lazy(() => import('features/auth/VerifyEmailPage'));
const AuthCallback = lazy(() => import('features/auth/AuthCallback'));
const GoogleCallbackPage = lazy(() => import('features/auth/GoogleCallbackPage'));
const ExplorePage = lazy(() => import('features/explore/ExplorePage'));
const OrderPage = lazy(() => import('features/orders/OrderPage'));
const ProductDetailPage = lazy(() => import('features/products/ProductDetailPage'));
const ProductCatalog = lazy(() => import('features/products/ProductCatalog'));
const ProcuredImagesPage = lazy(() => import('features/products/ProcuredImagesPage'));
const PlatformOptionsPage = lazy(() => import('features/platform/PlatformOptionsPage'));
const DocumentationRoutes = lazy(() => import('features/documentation/DocumentationRoutes'));
const FeedbackPage = lazy(() => import('features/feedback/FeedbackPage'));
const ImintRoutes = lazy(() => import('features/imint/ImintRoutes'));

const PageLoader = () => <LoadingSpinner fullPage />;


/**
 * Keeps ExplorePage mounted (preserving the Leaflet map and all state)
 * even when navigating to other routes. Hidden via CSS when inactive.
 */
const PersistentExplorePage = () => {
  const location = useLocation();
  const auth = useAuth();
  const isActive = location.pathname === '/explore';
  const [hasLoaded, setHasLoaded] = useState(false);

  useEffect(() => {
    if (isActive && !hasLoaded && auth.isAuthenticated) setHasLoaded(true);
  }, [isActive, hasLoaded, auth.isAuthenticated]);

  // Don't mount until the user has FIRST visited /explore while authenticated.
  // After that, keep the map MOUNTED across transient auth blips (a silent-renew
  // gap, cross-tab churn) — gating on `auth.isAuthenticated` here tore the whole
  // Leaflet map (and layers like the heatmap) down and rebuilt it on every
  // flicker. A genuine logout is handled by ProtectedRoute, which redirects away
  // from /explore, so this just hides via `isActive` — the map is never destroyed.
  if (!hasLoaded) return null;

  return (
    <div
      style={{
        visibility: isActive ? 'visible' : 'hidden',
        pointerEvents: isActive ? 'auto' : 'none',
        position: isActive ? 'relative' : 'fixed',
        zIndex: isActive ? undefined : -1,
        height: '100%',
      }}
    >
      <ExplorePage />
    </div>
  );
};

/**
 * Exclusive IMINT shell: once auth has settled, a user holding the `imint` role
 * is confined to /imint (the prototype TopNav + IMINT content are their whole
 * app). Non-imint users simply have no link to /imint.
 */
const ImintShellRedirect = () => {
  const auth = useAuth();
  const isImintUser = useIsImintUser();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (auth.isLoading || !auth.isAuthenticated) return;
    const path = location.pathname;
    if (
      path === '/login' ||
      path === '/create-new-account' ||
      path === '/verify-email' ||
      path === '/auth/callback' ||
      path === '/auth/google/callback'
    )
      return;
    if (isImintUser && !path.startsWith('/imint')) {
      navigate('/imint', { replace: true });
    }
  }, [auth.isLoading, auth.isAuthenticated, isImintUser, location.pathname, navigate]);

  return null;
};

const App = () => {
  return (
    <Router>
      <TokenSync />
      <AnalyticsProvider />
      <ImintShellRedirect />
      <SessionGuard
        timeoutMs={env.session.idleTimeoutMs}
        maxSessionMs={env.session.maxMs}
        warningMs={env.session.warningMs}
      />
      <CrossTabSync />
      <ToastProvider />

      <Layout>
        <ErrorBoundary>
          <Suspense fallback={<PageLoader />}>
            <PersistentExplorePage />
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/create-new-account" element={<RequestAccessPage />} />
              <Route path="/verify-email" element={<VerifyEmailPage />} />
              <Route path="/auth/callback" element={<AuthCallback />} />
              <Route path="/auth/google/callback" element={<GoogleCallbackPage />} />
              <Route element={<ProtectedRoute />}>
                <Route path="/" element={<LoginPage />} />
                {/* /explore is rendered by PersistentExplorePage (above) so the
                    Leaflet map stays mounted across navigation. The route here
                    exists only so React Router doesn't warn "no routes matched"
                    and so ProtectedRoute gates the auth-loading state for it. */}
                <Route path="/explore" element={null} />
                <Route path="/checkout" element={<Navigate to="/explore" replace />} />
                <Route path="/orders" element={<OrderPage />} />
                <Route path="/products" element={<ProductCatalog />} />
                <Route path="/products/procured" element={<ProcuredImagesPage />} />
                <Route path="/platform" element={<PlatformOptionsPage />} />
                <Route path="/products/:productId" element={<ProductDetailPage />} />
                <Route path="/documentation/*" element={<DocumentationRoutes />} />
                <Route path="/feedback" element={<FeedbackPage />} />
                <Route path="/imint/*" element={<ImintRoutes />} />
              </Route>
              {/* Any unknown URL falls back to login. Authenticated users are
                  then bounced on to /explore by LoginPage; everyone else signs in. */}
              <Route path="*" element={<Navigate to="/login" replace />} />
            </Routes>
          </Suspense>
        </ErrorBoundary>
      </Layout>
    </Router>
  );
};

export default App;
